/**
 * Recurring Charges Section Component
 * Allows landlord to manage recurring charges (rent, pet rent, parking, etc.)
 */

import { useState } from 'react';
import {
  type LeaseChargeConfig,
  type CreateChargeConfigInput,
  type UpdateChargeConfigInput,
  type ChargeType,
} from '../../lib/api';
import { HelpBlock } from '../ui/HelpTooltip';

interface RecurringChargesSectionProps {
  chargeConfigs: LeaseChargeConfig[];
  onCreate: (data: CreateChargeConfigInput) => Promise<void>;
  onUpdate: (chargeConfigId: string, data: UpdateChargeConfigInput) => Promise<void>;
  onDelete: (chargeConfigId: string) => void;
}

export function RecurringChargesSection({
  chargeConfigs,
  onCreate,
  onUpdate,
  onDelete,
}: RecurringChargesSectionProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<LeaseChargeConfig | null>(null);
  const [deletingCharge, setDeletingCharge] = useState<LeaseChargeConfig | null>(null);

  const activeCharges = chargeConfigs.filter(c => c && c.isActive);

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6" data-testid="recurring-charges-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="text-lg font-semibold m-0">
              Recurring Charges
            </h3>
            <p className="text-sm text-muted-foreground mt-1 m-0">
              Set up charges that recur monthly (rent, pet rent, parking, etc.)
            </p>
            <div className="mt-3">
              <HelpBlock
                summary="Recurring charges are automatically added to the tenant's ledger each month. The primary rent charge is created when the lease starts — use this section to add additional monthly charges."
                details={"Common additional charges: pet rent, covered parking, storage units, utility pass-throughs, or other fees specified in the lease agreement.\n\nEach charge can have its own due day, or default to the same day as rent. Charges appear as line items on the tenant's ledger and are included in payment calculations, past-due tracking, and late fee assessments.\n\nDeleting a charge stops future generation but does not remove charges already posted to the ledger."}
              />
            </div>
          </div>
          <button
            data-testid="add-charge-button"
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 min-h-[44px] shrink-0"
          >
            + Add Charge
          </button>
        </div>

        {activeCharges.length > 0 ? (
          <div className="grid gap-3" data-testid="charges-list">
            {activeCharges.map(charge => (
              <ChargeCard
                key={charge.id}
                charge={charge}
                onEdit={() => setEditingCharge(charge)}
                onDelete={() => setDeletingCharge(charge)}
              />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center bg-accent/50 rounded-md">
            <div className="text-5xl mb-3">💰</div>
            <p className="text-base text-muted-foreground m-0">
              No recurring charges configured
            </p>
            <p className="text-sm text-muted-foreground mt-1 m-0">
              Add charges like rent, pet rent, or parking fees
            </p>
          </div>
        )}
      </div>

      {isAddModalOpen && (
        <ChargeFormModal
          title="Add Recurring Charge"
          onSave={async (data) => {
            await onCreate(data as CreateChargeConfigInput);
            setIsAddModalOpen(false);
          }}
          onCancel={() => setIsAddModalOpen(false)}
        />
      )}

      {editingCharge && (
        <ChargeFormModal
          title="Edit Recurring Charge"
          charge={editingCharge}
          onSave={async (data) => {
            await onUpdate(editingCharge.id, data as UpdateChargeConfigInput);
            setEditingCharge(null);
          }}
          onCancel={() => setEditingCharge(null)}
        />
      )}

      {deletingCharge && (
        <DeleteChargeModal
          charge={deletingCharge}
          onConfirm={() => {
            onDelete(deletingCharge.id);
            setDeletingCharge(null);
          }}
          onCancel={() => setDeletingCharge(null)}
        />
      )}
    </>
  );
}

/**
 * Charge Card Component
 */
interface ChargeCardProps {
  charge: LeaseChargeConfig;
  onEdit: () => void;
  onDelete: () => void;
}

function ChargeCard({ charge, onEdit, onDelete }: ChargeCardProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getChargeTypeLabel = (type: ChargeType): string => {
    const labels: Record<ChargeType, string> = {
      rent: 'Rent',
      pet_rent: 'Pet Rent',
      parking: 'Parking',
      utilities: 'Utilities',
      storage: 'Storage',
      other: 'Other',
    };
    return labels[type] || type;
  };

  return (
    <div
      data-testid={`charge-row-${charge.id}`}
      className="bg-card rounded-lg border border-border p-4 flex justify-between items-center"
    >
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-base font-semibold m-0">
            {getChargeTypeLabel(charge.chargeType)}
          </h4>
        </div>
        <p className="text-sm text-muted-foreground mt-1 m-0">
          {formatCurrency(charge.amount / 100)}/month
          {charge.generationDay ? ` • Due: ${charge.generationDay}${getDaySuffix(charge.generationDay)}` : ' • Due: Same as rent'}
        </p>
        {charge.description && (
          <p className="text-sm text-muted-foreground mt-1 m-0">
            {charge.description}
          </p>
        )}
      </div>

      <div className="flex gap-2 shrink-0 ml-4">
        <button
          data-testid={`edit-charge-${charge.id}`}
          onClick={onEdit}
          className="px-3 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
        >
          Edit
        </button>
        <button
          data-testid={`delete-charge-${charge.id}`}
          onClick={onDelete}
          className="px-3 py-1.5 text-sm font-medium text-destructive bg-card border border-destructive/20 rounded-md hover:bg-destructive/5 min-h-[44px]"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/**
 * Charge Form Modal Component
 */
interface ChargeFormModalProps {
  title: string;
  charge?: LeaseChargeConfig;
  onSave: (data: CreateChargeConfigInput | UpdateChargeConfigInput) => Promise<void>;
  onCancel: () => void;
}

type ChargeFormData = CreateChargeConfigInput | UpdateChargeConfigInput;

function ChargeFormModal({ title, charge, onSave, onCancel }: ChargeFormModalProps) {
  const [chargeType, setChargeType] = useState<ChargeType>(charge?.chargeType ?? 'pet_rent');
  const [amount, setAmount] = useState<string>(charge?.amount != null ? (charge.amount / 100).toString() : '');
  const [description, setDescription] = useState<string>(charge?.description ?? '');
  const [generationDay, setGenerationDay] = useState<string>(charge?.generationDay?.toString() ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      let data: ChargeFormData;

      if (charge) {
        data = {
          amount: Math.round(parseFloat(amount) * 100),
          description: description || undefined,
          generationDay: generationDay ? parseInt(generationDay) : null,
        } as UpdateChargeConfigInput;
      } else {
        data = {
          chargeType,
          amount: Math.round(parseFloat(amount) * 100),
          description: description || undefined,
          generationDay: generationDay ? parseInt(generationDay) : null,
        } as CreateChargeConfigInput;
      }

      await onSave(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save charge');
    } finally {
      setIsSaving(false);
    }
  };

  const chargeTypeOptions: { value: ChargeType; label: string }[] = [
    { value: 'pet_rent', label: 'Pet Rent' },
    { value: 'parking', label: 'Parking' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'storage', label: 'Storage' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div
      data-testid="charge-form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="charge-form-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="charge-form-title" className="text-xl font-semibold m-0">
          {title}
        </h3>

        {error && (
          <div
            data-testid="charge-form-error"
            role="alert"
            aria-live="polite"
            className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 mt-4"
          >
            <p className="text-sm text-destructive m-0">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="charge-form" className="mt-4">
          <div className="grid gap-4">
            {!charge && (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="charge-type"
                  className="text-sm font-medium text-foreground"
                >
                  Charge Type *
                </label>
                <select
                  id="charge-type"
                  data-testid="charge-type-input"
                  value={chargeType}
                  onChange={(e) => setChargeType(e.target.value as ChargeType)}
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
                >
                  {chargeTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-amount"
                className="text-sm font-medium text-foreground"
              >
                Amount ($) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-base">
                  $
                </span>
                <input
                  id="charge-amount"
                  data-testid="charge-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  aria-required="true"
                  className="w-full px-3 py-2 pl-7 text-sm border border-input rounded-md min-h-[44px]"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-description"
                className="text-sm font-medium text-foreground"
              >
                Description (Optional)
              </label>
              <input
                id="charge-description"
                data-testid="charge-description-input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Covered parking spot"
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="charge-due-day"
                className="text-sm font-medium text-foreground"
              >
                Due Day (Optional)
              </label>
              <select
                id="charge-due-day"
                data-testid="charge-due-day-input"
                value={generationDay}
                onChange={(e) => setGenerationDay(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md min-h-[44px]"
              >
                <option value="">Same as rent due day</option>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>
                    {day}{getDaySuffix(day)} of the month
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1 m-0">
                Leave blank to use the same due day as rent
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              data-testid="charge-form-cancel"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="charge-form-submit"
              disabled={isSaving}
              aria-disabled={isSaving}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isSaving ? 'Saving...' : charge ? 'Update Charge' : 'Add Charge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Delete Charge Modal Component
 */
interface DeleteChargeModalProps {
  charge: LeaseChargeConfig;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteChargeModal({ onConfirm, onCancel }: DeleteChargeModalProps) {
  return (
    <div
      data-testid="delete-charge-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-charge-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-lg max-w-sm w-[90%] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="delete-charge-title" className="text-xl font-semibold m-0">
          Delete Charge?
        </h3>
        <p className="text-base text-foreground mt-3 m-0">
          Are you sure you want to delete this charge? This action cannot be undone.
        </p>

        <div className="flex gap-3 mt-6">
          <button
            data-testid="delete-charge-cancel"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-accent min-h-[44px]"
          >
            Cancel
          </button>
          <button
            data-testid="delete-charge-confirm"
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-destructive-foreground bg-destructive rounded-md hover:bg-destructive/90 min-h-[44px]"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
